import type { CSSProperties, MouseEvent, MouseEventHandler, ReactNode } from 'react';
import { C } from '@/theme/tokens';

export type GoldBtnVariant = 'primary' | 'outlined' | 'ghost';
export type GoldBtnSize = 'sm' | 'md' | 'lg';

export interface GoldBtnProps {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  variant?: GoldBtnVariant;
  size?: GoldBtnSize;
  href?: string;
  style?: CSSProperties;
}

/**
 * The signature gold/bronze art-deco button used everywhere in the page.
 * Renders an `<a>` when `href` is provided, otherwise a `<button>`.
 *
 * Hover effects are applied imperatively to match the original reference 1:1
 * (instead of pseudo-classes) so primary, outlined, and ghost variants each
 * have their own micro-interaction without leaning on a CSS-in-JS lib.
 */
export function GoldBtn({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  href,
  style = {},
}: GoldBtnProps) {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 30,
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    textDecoration: 'none',
    padding: size === 'lg' ? '14px 36px' : size === 'sm' ? '7px 18px' : '10px 26px',
    fontSize: size === 'lg' ? 15 : size === 'sm' ? 12 : 13,
  };

  const contained: CSSProperties = {
    background: 'linear-gradient(135deg, #D4AF37, #C19A6B)',
    color: '#0a1a2a',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  };
  const outlined: CSSProperties = {
    background: 'transparent',
    color: C.bronze,
    border: `1px solid ${C.bronze}`,
  };
  const ghost: CSSProperties = {
    background: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.25)',
  };

  const variantStyle =
    variant === 'primary' ? contained : variant === 'outlined' ? outlined : ghost;

  const handleEnter = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const t = e.currentTarget;
    if (variant === 'primary') {
      t.style.transform = 'translateY(-2px)';
      t.style.boxShadow = '0 6px 20px rgba(212,175,55,0.4)';
      t.style.background = 'linear-gradient(135deg,#FFD740,#D4AF37)';
    } else if (variant === 'outlined') {
      t.style.color = C.gold;
      t.style.borderColor = C.gold;
      t.style.background = 'rgba(212,175,55,0.08)';
    } else {
      t.style.background = 'rgba(255,255,255,0.08)';
    }
  };

  const handleLeave = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const t = e.currentTarget;
    t.style.transform = '';
    t.style.boxShadow = variant === 'primary' ? '0 4px 12px rgba(0,0,0,0.4)' : '';
    if (variantStyle.background) {
      t.style.background = variantStyle.background as string;
    }
    if (variantStyle.color) {
      t.style.color = variantStyle.color as string;
    }
    if (typeof variantStyle.border === 'string') {
      const parts = variantStyle.border.split(' ');
      t.style.borderColor = parts[parts.length - 1] ?? '';
    }
  };

  const merged: CSSProperties = { ...base, ...variantStyle, ...style };

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
        style={merged}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'scale(0.98)';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick as MouseEventHandler<HTMLButtonElement>}
      style={merged}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = 'scale(0.98)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
    >
      {children}
    </button>
  );
}
